import { type LevelInfo } from '@/hooks/usePoints';

interface LevelCardProps {
  points: number;
  levelInfo?: LevelInfo;
  levels?: LevelInfo[];
  onGoPoints?: () => void;
}

const UNKNOWN_LEVEL: LevelInfo = {
  level: 0,
  name: '未配置',
  title: '待配置',
  minPoints: 0,
  maxPoints: Infinity,
  color: '#94a3b8',
  icon: 'ri-question-line',
};

export default function LevelCard({ points, levelInfo, levels, onGoPoints }: LevelCardProps) {
  const resolvedLevelInfo = levelInfo ?? UNKNOWN_LEVEL;
  const resolvedLevels = Array.isArray(levels) ? levels : [];
  const hasLevels = resolvedLevels.length > 0;
  const nextLevel = resolvedLevels.find((l) => l.level === resolvedLevelInfo.level + 1);
  const progress = nextLevel
    ? Math.round(((points - resolvedLevelInfo.minPoints) / (nextLevel.minPoints - resolvedLevelInfo.minPoints)) * 100)
    : 100;

  return (
    <div
      className="rounded-2xl overflow-hidden border border-gray-100 relative cursor-pointer group"
      style={{ background: `linear-gradient(135deg, ${resolvedLevelInfo.color}12 0%, ${resolvedLevelInfo.color}06 100%)` }}
      onClick={onGoPoints}
    >
      {/* Decorative glow */}
      <div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10 pointer-events-none"
        style={{ backgroundColor: resolvedLevelInfo.color }}
      ></div>

      <div className="relative p-5">
        {/* Top row: level icon + info + points number */}
        <div className="flex items-center gap-4">
          {/* Level icon */}
          <div
            className="w-16 h-16 flex items-center justify-center rounded-2xl shrink-0 shadow-sm"
            style={{ backgroundColor: `${resolvedLevelInfo.color}20`, border: `2px solid ${resolvedLevelInfo.color}40` }}
          >
            <i className={`${resolvedLevelInfo.icon} text-3xl`} style={{ color: resolvedLevelInfo.color }}></i>
          </div>

          {/* Level name & title */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xl font-bold text-gray-900">{resolvedLevelInfo.name}</span>
              <span
                className="text-xs font-bold px-2.5 py-0.5 rounded-full text-white"
                style={{ backgroundColor: resolvedLevelInfo.color }}
              >
                {resolvedLevelInfo.title}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
              <i className="ri-copper-coin-line text-amber-400"></i>
              累计 <strong className="text-gray-800 mx-0.5">{points.toLocaleString()}</strong> 灵石
            </p>
          </div>

          {/* Big points number */}
          <div className="text-right shrink-0">
            <div className="text-3xl font-black" style={{ color: resolvedLevelInfo.color }}>
              {points.toLocaleString()}
            </div>
            <div className="text-xs text-gray-400 mt-0.5 whitespace-nowrap">灵石总量</div>
          </div>
        </div>

        {/* Progress bar to next level */}
        <div className="mt-4">
          {!hasLevels ? (
            <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50">
              <i className="ri-information-line text-gray-400"></i>
              <p className="text-xs text-gray-500">暂无等级配置</p>
            </div>
          ) : nextLevel ? (
            <>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <i className="ri-arrow-up-line" style={{ color: resolvedLevelInfo.color }}></i>
                  升级进度
                </span>
                <span className="text-xs font-semibold" style={{ color: resolvedLevelInfo.color }}>
                  {progress}%
                  <span className="text-gray-400 font-normal ml-1">
                    · 距《{nextLevel.name}》还差 {(nextLevel.minPoints - points).toLocaleString()} 灵石
                  </span>
                </span>
              </div>
              {/* Progress track */}
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${Math.min(progress, 100)}%`,
                    background: `linear-gradient(90deg, ${resolvedLevelInfo.color}99, ${resolvedLevelInfo.color})`,
                  }}
                ></div>
              </div>
              {/* Range labels */}
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{resolvedLevelInfo.minPoints.toLocaleString()} 灵石</span>
                <span>{nextLevel.minPoints.toLocaleString()} 灵石</span>
              </div>
            </>
          ) : (
            <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-xl" style={{ backgroundColor: `${resolvedLevelInfo.color}15` }}>
              <i className="ri-rocket-2-line text-lg" style={{ color: resolvedLevelInfo.color }}></i>
              <div>
                <p className="text-sm font-bold" style={{ color: resolvedLevelInfo.color }}>已臻化境 · 大乘飞升</p>
                <p className="text-xs text-gray-500">山河苍生，天地共色——已达最高境界！</p>
              </div>
            </div>
          )}
        </div>

        {/* Mini level milestones row */}
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
          {resolvedLevels.map((l) => {
            const reached = points >= l.minPoints;
            const isCurrent = resolvedLevelInfo.level === l.level;
            return (
              <div
                key={l.level}
                className="flex flex-col items-center gap-1 shrink-0"
                title={`${l.name} · ${l.title} (${l.minPoints.toLocaleString()}灵石)`}
              >
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-lg transition-all"
                  style={{
                    backgroundColor: reached ? `${l.color}20` : '#f3f4f6',
                    border: isCurrent ? `2px solid ${l.color}` : '2px solid transparent',
                  }}
                >
                  <i
                    className={`${l.icon} text-xs`}
                    style={{ color: reached ? l.color : '#d1d5db' }}
                  ></i>
                </div>
                <span className="text-xs whitespace-nowrap" style={{ color: isCurrent ? l.color : reached ? '#6b7280' : '#d1d5db', fontWeight: isCurrent ? 700 : 400 }}>
                  {l.name.slice(0, 2)}
                </span>
              </div>
            );
          })}
          {resolvedLevels.length === 0 && <span className="text-xs text-gray-400">暂无等级配置</span>}
          <div className="ml-auto shrink-0">
            <span className="text-xs text-gray-400 flex items-center gap-0.5 group-hover:text-gray-600 transition-colors whitespace-nowrap">
              灵石中心 <i className="ri-arrow-right-s-line"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
